import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { orderBy } from "lodash-es";
import { usePageVisibility } from "react-page-visibility";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { useSelector } from "react-redux";
import { Bloodhound } from "common/components/typeahead";
import levenshtein from "js-levenshtein";

import { captureException } from "extensions/sentry";
import { emitter } from "common/emitter";

import { getScrollbarWidth } from "common/utils";

// Gives a ref to a valid target for ReactDOM.createPortal rooted at `root` ref.
export const usePortalEl = (root, portalId) => {
    const portalEl = useRef(document.getElementById(portalId));

    if (!portalEl.current) {
        portalEl.current = document.createElement("div");
        portalEl.current.setAttribute("id", portalId);
        root.current.appendChild(portalEl.current);
    }

    return portalEl;
};

// Conditionally disallow page scrolling. NOTE: Maybe not cleanest solution, but necessary since React does not allow conditionally calling a hook.
export const useNoScroll = (when) => {
    useEffect(() => {
        if (when) {
            const cl = document.body.classList;
            const oldPadding = document.body.style.paddingRight;
            if (!cl.contains("noscroll")) {
                cl.add("noscroll");
                document.body.style.paddingRight = `${getScrollbarWidth}px`;
            }

            return () => {
                document.body.style.paddingRight = oldPadding;
                document.body.classList.remove("noscroll");
            };
        }
    }, [when]);
};

// Conditionally set a dom node's position at the current scrollY. NOTE: Maybe not cleanest solution, but necessary since React does not allow conditionally calling a hook.
export const useScrollY = (when, ref) => useEffect(() => { if (when) { ref.current.style.top = `${window.scrollY}px`; } }, [when, ref]);

// Use a global key binding for the duration of a component's lifetime.
export const useKeyboardHandler = (key, effect, event = "keyup", preventDefault = true, stopPropagation = true) => {
    useEffect(() => {
        const handleKeyboard = (e) => {
            if (e.key === key) {
                if (preventDefault) e.preventDefault();
                if (stopPropagation) e.stopPropagation();
                effect();
            }
        };
        window.addEventListener(event, handleKeyboard);
        return () => window.removeEventListener(event, handleKeyboard);
    }, [key, effect, event, preventDefault, stopPropagation]);
};

const sort_icons = {
    desc: <FontAwesomeIcon className="sort-order-icon" icon={faChevronDown} />,
    asc: <FontAwesomeIcon className="sort-order-icon" icon={faChevronUp} />
};

/**
 * Sort a collection of data by the given key with the given initial sort order.
 *
 * @param {Array} data - the collection to be sorted.
 * @param {string | function} initialSort - the property of each piece of data to sort by, or the function to sort with. The second argument to lodash `orderBy`.
 * @param {("asc" | "desc")} initialSortOrder - The direction to sort by.
 * @returns {Array}            0: sorted - the sorted data.
 * @returns {string}           1: sort - the key data is currently sorted by.
 * @returns {function}         2: resort - resort the data with a new key. If key is the same, resort the data with the opposite direction.
 * @returns {("asc" | "desc")} 3: order - The direction data is currently sorted by.
 * @returns {React.Component}  4: icon - An icon representing the current sort order.
 */
export const useSortedData = (data, initialSort, initialSortOrder = "asc") => {
    const [sort, setSort] = useState(() => initialSort);
    const [order, setOrder] = useState(initialSortOrder);

    const toggleOrder = () => setOrder(order === "asc" ? "desc" : "asc");

    const sorted = useMemo(() => orderBy(data, sort, [order]), [data, sort, order]);

    const resort = (value) => () => {
        if (sort === value) {
            toggleOrder();
        } else {
            setSort(() => value);
            setOrder(initialSortOrder);
        }
    };

    return [sorted, sort, resort, order, sort_icons[order]];
};

/**
 * Poll API for data. Polls on the next react rerender after delay has passed.
 * @param {function} fetch - Nullary function returning a promise with the desired data.
 * @param {boolean} when - When to poll. If false, will stop polling. If true will continue polling.
 * @param {integer} delay - How long to wait (in milliseconds) before triggering the next fetch.
 * @param {Any} initialData - Passed back on the very first invocation of hook before first fetch is fired.
 * @param {function} onError - Unary function accepting an error object defining what should be done if fetch throws an exception. Do not use `catch` on fetch because this hook must intercept first.
 * @param {bool} continueOnError - If false will not poll again after the first exception. If true will always retry fetch even after exception.
 * @returns {Any} data - The data most recently returned by fetch.
 * @returns {function} stopPolling - Nullary function to stop the hook from polling.
 * @returns {function} resetPolling - Nullary function to continue normal polling behavior (dependent on `when`).
 */
export const usePolling = ({
    fetch,
    when = true,
    delay,
    initialData,
    onError = (err) => captureException(err),
    continueOnError = true
}) => {
    const [data, setData] = useState(initialData);
    const [pollingTrigger, setPollingTrigger] = useState(true);
    const sessionTimeoutModalShown = useSelector((state) => state.authentication.sessionTimeoutModal);
    const pollingEnabled = useSelector((state) => state.settings.polling);
    const isPageVisible = usePageVisibility();
    const [shouldPoll, setShouldPoll] = useState(pollingEnabled && isPageVisible && !sessionTimeoutModalShown && when);
    
    const stopPolling = () => setShouldPoll(false);
    const resetPolling = useCallback(() => setShouldPoll(pollingEnabled && isPageVisible && !sessionTimeoutModalShown && when), [setShouldPoll, pollingEnabled, isPageVisible, sessionTimeoutModalShown, when]);
    useEffect(() => setShouldPoll(pollingEnabled && isPageVisible && !sessionTimeoutModalShown && when), [pollingEnabled, isPageVisible, sessionTimeoutModalShown, when]);
    const systemPollingDelay = useSelector((state) => state.config.notificationFetchInterval);
    
    useEffect(() => {
        emitter.on("logout:before", stopPolling);
        emitter.on("polling:stop", stopPolling);
        emitter.on("polling:reset", resetPolling);
        
        return () => {
            emitter.off("logout:before", stopPolling);
            emitter.off("polling:stop", stopPolling);
            emitter.off("polling:reset", resetPolling);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resetPolling]);

    useEffect(() => {
        if (shouldPoll) {
            fetch().then((newData) => {
                setData(newData);
                Promise.delay(delay ?? systemPollingDelay).then(() => {
                    // trigger another fetch on the next rerender.
                    setPollingTrigger(!pollingTrigger);
                });
            }).catch((error) => {
                if (!continueOnError) {
                    // don't trigger again
                    throw error;
                }
                // OK to trigger again.
                Promise.delay(delay ?? systemPollingDelay).then(() => {
                    setPollingTrigger(!pollingTrigger);
                });
            }).catch(onError);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pollingTrigger, shouldPoll]);

    return { data, stopPolling, resetPolling };
};

export const useDocumentClickOutsideTarget = (effect, targetSelector) => {
    useEffect(() => {
        const handleClick = (e) => {
            if (!e.target.closest(targetSelector)) {
                effect();
            }
        };
        window.addEventListener("click", handleClick);
        return () => window.removeEventListener("click", handleClick);
    }, [effect, targetSelector]);
};

const mod = (upperBound) => (n) => n % upperBound;
const bounds = (upperBound) => (n) => Math.min(Math.max(n, 0), upperBound - 1);

// returns the natural number representing the sum of the cumulative times the user hit the increment key minus the sum of the cumulative times the user hit the decrement key.
export const useLinearKeyboardNavigation = ({ upperBound, wraps = false, incrementKey = "ArrowDown", decrementKey = "ArrowUp" }) => {
    const [selected, setSelected] = useState(0);
    const wrapFn = wraps ? mod(upperBound) : bounds(upperBound);
    useKeyboardHandler(incrementKey, () => setSelected(wrapFn(selected + 1)), "keydown");
    useKeyboardHandler(decrementKey, () => setSelected(wrapFn(selected - 1)), "keydown");

    // reset whenever the allowed bounds change.
    useEffect(() => {
        setSelected(0);
    }, [upperBound]);

    return selected;
};

// Return the filtered list of results for a local collection of objects based on levenshtein distance from an input string.
export const useTypeAhead = ({
    data,
    typeAheadFields,
    idField = "id",
    sufficient = 100,
    typeAheadInputString
}) => {
    const inputString = useRef(null);
    const bh = useRef(null);

    // Keep bloodhound's reference to input up to date with the user's typed input.
    inputString.current = typeAheadInputString;

    // Only recreate the typeahead engine if the configuration fields change, NOT the typeahead field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        bh.current = new Bloodhound({
            datumTokenizer: Bloodhound.tokenizers.obj.whitespace(typeAheadFields),
            queryTokenizer: Bloodhound.tokenizers.whitespace,
            local: data,
            identify: (obj) => obj[idField],
            sorter: (a, b) => {
                const input_string = inputString.current; // use the current value of the user input field.
                return levenshtein(a.display_name, input_string) - levenshtein(b.display_name, input_string);
            },
            sufficient
        });
        return () => bh.current.clear();
    }, [data, typeAheadFields, idField, sufficient]);

    // On mount just return the whole collection, until Bloodhound initializes;
    let retVal = data;
    if (!typeAheadInputString || !typeAheadInputString.length) {
        // On empty input, all data are viable (by default bloodhound returns []).
        return retVal;
    }
    if (data && bh.current) {
        bh.current.search(typeAheadInputString, (res) => { retVal = res; });
    }
    return retVal;
};

/**
 * Hook that produce action on clicks outside of the passed ref
 * @param {React.Ref} ref - React reference of container that needs to be monitored for outside clicks.
 * @param {function} actionFn - function to call on outside click.
 * @param {Boolean} mute - mute action flag
 */
export const useClickAwayDetector = (ref, actionFn) => {
    useEffect(() => {
        /**
         * Call a  if clicked on outside of element
         */
        function handleClickOutside(event) {
            if (ref.current && !ref.current.contains(event.target)) {
                actionFn();
            }
        }

        // Bind the event listener
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            // Unbind the event listener on clean up
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [ref, actionFn]);
};
